import * as _ from 'lodash';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';

import { combineLatest, Subject } from 'rxjs';

import { PropertyRecordService } from '../../../../shared/services/';

import { PropertyRecord } from '../../../../shared/models';
import IBuildingZoningAllowance = ZoningAPI.IBuildingZoningAllowance;
import ICalculationResult = ZoningAPI.ICalculationResult;
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialogConfig, MatDialog } from '@angular/material/dialog';
import { CalculationParametersInfoComponent } from './calculation-parameters-info/calculation-parameters.component';
import { HttpParams } from '@angular/common/http';
import { InstanceService } from '../../services/instance.service';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
    standalone: false,
    selector: 'app-property-records-tab',
    templateUrl: './property-records-tab.component.html',
    styleUrls: ['./property-records-tab.component.scss', '../../../../shared/styles/data-grid.scss']
})
export class PropertyRecordsTabComponent implements OnInit, OnDestroy {
    constructor(
        private readonly propertyRecordService: PropertyRecordService,
        private readonly dialog: MatDialog,
        private readonly instanceService: InstanceService,
        private readonly snackBar: MatSnackBar
    ) {
        this.propertyRecordService.isLoading
            .pipe(takeUntil(this.destroy$))
            .subscribe(isLoading => (this.isLoading = isLoading));
    }

    searchString = new FormControl('');

    propertyRecords: PropertyRecord[];

    propertyRecordsColumns: string[] = ['Id', 'GridicsId', 'GroupId', 'FolioNumber', 'Address'];
    propertyRecordsColumnsToDisplay: string[] = [...this.propertyRecordsColumns, 'Actions'];

    zoningCodeVersionColumns: string[] = ['Id', 'ZoningCodeId', 'ActualVersion'];
    calculationResultsColumns: string[] = ['ZoningAllowanceId'];

    expandedElement: PropertyRecord;

    showResultsCalculatedWithParams = false;
    lastSelectedInstanceId: number;

    showSpinner: boolean;

    isRecalcWithPropertyRecord = false;
    isLoading = false;

    private destroy$ = new Subject<void>();

    get lastZCVersion(): number {
        const lastElementIndex =
            this.expandedElement && this.expandedElement.CalculationResults
                ? this.expandedElement.CalculationResults.length - 1
                : null;
        const calculationResult =
            lastElementIndex !== null ? this.expandedElement.CalculationResults[lastElementIndex] : null;
        return (
            calculationResult &&
            calculationResult.ZoningCodeVersion &&
            calculationResult.ZoningCodeVersion.ActualVersion
        );
    }

    get calculationResultsColumnsToDisplay(): string[] {
        if (!this.showResultsCalculatedWithParams) {
            return [...this.zoningCodeVersionColumns, 'Actions'];
        }
        return [...this.zoningCodeVersionColumns, ...this.calculationResultsColumns, 'Actions'];
    }

    get showPropertyRecord(): boolean {
        return (
            this.instanceService.selectedInstance &&
            this.instanceService.selectedInstance.Id === this.lastSelectedInstanceId
        );
    }

    ngOnInit() {
        combineLatest([
            this.propertyRecordService.propertyRecordList,
            this.instanceService.currentInstance.pipe(filter(instance => instance !== null))
        ]).subscribe(([prs, instance]) => {
            if (instance.Id !== this.lastSelectedInstanceId) {
                this.propertyRecords = [];
                this.expandedElement = null;
                return;
            }
            this.propertyRecords = prs;
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next(undefined);
        this.destroy$.complete();
    }

    searchHandler() {
        if (!this.searchString.value || this.showSpinner) {
            return;
        }

        this.showSpinner = true;
        this.lastSelectedInstanceId = this.instanceService.selectedInstance.Id;
        const subscription = this.propertyRecordService.getPropertyRecordList(this.searchString.value, true).subscribe(
            () => {
                this.showSpinner = false;
                subscription.unsubscribe();
            },
            () => {
                this.showSpinner = false;
                subscription.unsubscribe();
            }
        );
    }

    showCalculationResults(element: PropertyRecord) {
        if (this.isLoading) return;
        if (this.expandedElement && this.expandedElement.Id === element.Id) {
            this.expandedElement = null;
            return;
        }

        this.expandedElement = element;

        if (
            !this.expandedElement.hasCalculationResults ||
            this.expandedElement.areCalculationResultsWithParams !== this.showResultsCalculatedWithParams
        ) {
            this.updateCalculationResults();
        }
    }

    updateCalculationResults() {
        if (!this.expandedElement) {
            return;
        }
        const options = {
            showResultsCalculatedWithParams: this.showResultsCalculatedWithParams
        };
        this.propertyRecordService.getPropertyRecordVersions(this.expandedElement.Id, options);
    }

    redirectToReview(element: ICalculationResult) {
        const params = new HttpParams()
            .set('zcVersionId', String(element.ZoningCodeVersionId))
            .set('zaId', String(element.ZoningAllowanceId))
            .set('state_env', String(this.instanceService.selectedInstance.State));

        const url = `/property-record/${this.expandedElement.GridicsId}?`;
        window.open(url + params.toString());
    }

    recalculateLastResult(_$event, propertyRecord: PropertyRecord) {
        if (this.isLoading) return;
        this.isRecalcWithPropertyRecord = propertyRecord ? true : false;
        this.propertyRecordService.isLoading.next(true);
        const propertyRecordId = propertyRecord ? propertyRecord.Id : this.expandedElement.Id;
        const options = {
            needToRecalculateLastResult: true,
            showResultsCalculatedWithParams: this.showResultsCalculatedWithParams
        };
        this.propertyRecordService.getPropertyRecordVersions(propertyRecordId, options);

        event.stopPropagation();
    }

    showCalculationParametersInfo(event, zoningAllowancesParams: IBuildingZoningAllowance) {
        this.openCalcParamsDialog(zoningAllowancesParams);

        event.stopPropagation();
    }

    openCalcParamsDialog(zoningAllowancesParams: object) {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = '50%';
        zoningAllowancesParams = _.pickBy(zoningAllowancesParams, _.identity);
        dialogConfig.data = zoningAllowancesParams;
        this.dialog.open(CalculationParametersInfoComponent, dialogConfig);
    }

    showRecalculateButton(element: any) {
        return (
            element.ZoningCodeVersion &&
            element.ZoningCodeVersion.ActualVersion === this.lastZCVersion &&
            !element.BuildingZoningAllowance
        );
    }

    get showForceRecalculate(): boolean {
        return (
            this.propertyRecords &&
            this.propertyRecords.length === 0 &&
            this.lastSelectedInstanceId &&
            this.lastSelectedInstanceId === this.instanceService.selectedInstance.Id
        );
    }

    tryForcedCalculate() {
        if (this.showSpinner) {
            return;
        }
        this.showSpinner = true;

        const subscription = this.propertyRecordService.tryForcedCalculate(this.searchString.value).subscribe(
            () => {
                this.showSpinner = false;
                subscription.unsubscribe();
            },
            () => {
                this.snackBar.open('Can not calculate Property Record. API returns error.', 'ERROR', {
                    verticalPosition: 'top',
                    horizontalPosition: 'center',
                    duration: 2000
                });
                this.showSpinner = false;
                subscription.unsubscribe();
            }
        );
    }
}
